import { forwardRef, type PropsWithChildren, type ReactElement } from 'react';

import { TimeseriesChartLegend } from './components/legend/TimeseriesChartLegend.js';
import { TIMESERIES_CHART_DEFAULT_VARIANT } from './constants.js';
import { TimeseriesRawDataContext } from './context/TimeseriesRawData.context.js';
import { useTimeseriesChartConfig } from './hooks/useTimeseriesChartConfig.js';
import { StoreProvider } from './providers/StoreProvider.js';
import { TimeseriesChartIdProvider } from './providers/TimeseriesChartIdProvider.js';
import { TimeseriesConfigProvider } from './providers/TimeseriesConfigProviders.js';
import { TimeseriesDataProvider } from './providers/TimeseriesDataProvider.js';
import { TimeseriesFilteredDataProvider } from './providers/TimeseriesFilteredDataProvider.js';
import { TimeseriesMinMaxXAxisProvider } from './providers/TimeseriesMinMaxXAxisProvider.js';
import { TimeseriesChartInternal } from './TimeseriesChartInternal.js';
import { TimeseriesChartProps } from './types/timeseries-props.js';
import { TimeseriesChartRef } from './types/timeseries-ref.js';
import {
  hasSeriesWithDatapoints,
  splitTimeseriesPerVariant,
} from './utils/timeseries.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { ChartLayout } from '../core/components/chart-layout/ChartLayout.js';
import { useAutoLegendRefresh } from '../core/components/chart-layout/hooks/useAutoLegendRefresh.js';
import { getChartStatesTemplates } from '../core/components/chart-states/utils/get-chart-states-template.js';
import { OverlayProvider } from '../core/components/overlay-tooltip/contexts/OverlayContext.js';
import { TooltipProvider } from '../core/components/overlay-tooltip/contexts/TooltipContext.js';
import { ChartSeriesActionContext } from '../core/components/series-action/ChartSeriesActionContext.js';
import { coerceSizeValue } from '../core/utils/coerceSizeValue.js';

/**
 * The default max height used for the timeserieschart when no explicit
 * height is given
 */
const TIMESERIES_CHART_DEFAULT_HEIGHT = 300;

/**
 * Component name used for data-dt-component in behavioural tracking
 */
const COMPONENT_NAME_BEHAVIOURAL_TRACKING = 'TimeseriesChart';

/**
 * TimeseriesChart component
 */
export const TimeseriesChartRoot = forwardRef<
  TimeseriesChartRef,
  TimeseriesChartProps
>((props, forwardedRef) => {
  const {
    children,
    data,
    gapPolicy,
    pointsDisplay,
    colorPalette,
    truncationMode,
    variant = TIMESERIES_CHART_DEFAULT_VARIANT,
    valueRepresentation,
    height: heightProp,
    width,
    seriesActions,
    onMessage,
    loading = false,
    curve,
    initialZoom,
    currentZoom,
    onZoomChange,
    infiniteZoom,
    className,
    style,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
  } = props;
  const [behaviorTrackingProps] = useBehavioralTrackingProps(props);

  const chartHeight =
    coerceSizeValue(heightProp) || TIMESERIES_CHART_DEFAULT_HEIGHT;
  const dataPerVariant = splitTimeseriesPerVariant(
    data ?? [],
    children,
    variant,
  );
  const config = useTimeseriesChartConfig(children, {
    gapPolicy,
    pointsDisplay,
    valueRepresentation,
    colorPalette,
    truncationMode,
    curve,
    initialZoom,
    currentZoom,
    onZoomChange,
    infiniteZoom,
  });

  const { emptyState, errorState } = getChartStatesTemplates(children);

  const { shapePriority, ...rawDataPerVariant } = dataPerVariant;
  const hasSeries = hasSeriesWithDatapoints(dataPerVariant);

  const {
    position: legendPosition,
    onRatioChange,
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    ratio,
    initialRatio,
    hidden: isLegendHidden,
  } = config.legend;

  const legendRatio = initialRatio ?? ratio;

  const chartLayoutRef = useAutoLegendRefresh(data);

  return (
    <TimeseriesChartIdProvider>
      <TimeseriesConfigProvider config={config}>
        <TimeseriesRawDataContext.Provider value={rawDataPerVariant}>
          <TimeseriesDataProvider
            data={dataPerVariant}
            valueRepresentation={config.valueRepresentation}
            onMessage={onMessage}
          >
            <TimeseriesMinMaxXAxisProvider>
              <ChartSeriesActionContext.Provider
                value={{
                  chartSeriesActions: seriesActions as (
                    series: unknown,
                    datapoint?: unknown,
                  ) => ReactElement<PropsWithChildren>,
                }}
              >
                <StoreProvider
                  data={dataPerVariant}
                  currentDomain={config.currentZoom}
                  initialDomain={config.initialZoom}
                  infiniteZoom={config.infiniteZoom}
                >
                  <TimeseriesFilteredDataProvider>
                    <OverlayProvider>
                      <TooltipProvider>
                        <ChartLayout
                          ref={chartLayoutRef}
                          chartHeight={chartHeight}
                          chartWidth={width}
                          isEmpty={!hasSeries}
                          emptyState={emptyState}
                          errorState={errorState}
                          showLoader={loading}
                          className={className}
                          style={style}
                          {...behaviorTrackingProps}
                          data-dtrum-mask={dataDtrumMask}
                          data-dtrum-allow={dataDtrumAllow}
                          data-dt-component={
                            COMPONENT_NAME_BEHAVIOURAL_TRACKING
                          }
                        >
                          <ChartLayout.Graph>
                            <TimeseriesChartInternal
                              ref={forwardedRef}
                              config={config}
                            />
                          </ChartLayout.Graph>
                          {!isLegendHidden && (
                            <ChartLayout.Legend
                              position={legendPosition}
                              initialRatio={legendRatio}
                              onResize={onRatioChange}
                            >
                              <TimeseriesChartLegend />
                            </ChartLayout.Legend>
                          )}
                        </ChartLayout>
                      </TooltipProvider>
                    </OverlayProvider>
                  </TimeseriesFilteredDataProvider>
                </StoreProvider>
              </ChartSeriesActionContext.Provider>
            </TimeseriesMinMaxXAxisProvider>
          </TimeseriesDataProvider>
        </TimeseriesRawDataContext.Provider>
      </TimeseriesConfigProvider>
    </TimeseriesChartIdProvider>
  );
});

(
  TimeseriesChartRoot as typeof TimeseriesChartRoot & { displayName: string }
).displayName = 'TimeseriesChartRoot';
